/* An amount and its currency, as one control — STORY-020.
 *
 * The markup and class names are exactly those in `design/components/money-input`
 * — the mockup is the specification, and keeping them identical is what lets
 * the two be diffed.
 *
 * ── the two inputs are one value ────────────────────────────────────────────
 * ADR-0005: money is an integer in the minor unit plus an ISO 4217 code, never
 * a float and never a bare number. They are joined visually because neither
 * means anything alone — "12000" with no currency is not a smaller amount, it
 * is not an amount.
 *
 * ── the currency list is the ENABLED set, never all of ISO 4217 ─────────────
 * A workspace enables a handful; searching all ~180 happens in one place,
 * Settings. `i18n.md` owns that rule, and the hint below says where to go
 * rather than offering an inline enable this control would have to honour.
 *
 * A value already on a record is kept in the list even once it is disabled, and
 * stays selected — enablement governs pickers, never stored data, and dropping
 * it would silently rewrite somebody's record on the next save.
 */

import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

export interface MoneyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'value' | 'onChange'> {
  readonly label: string;
  readonly currencyLabel: string;
  /** What was typed, in MAJOR units and the reader's own separators. */
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly currency: string;
  readonly onCurrencyChange: (next: string) => void;
  /** The workspace's enabled set. */
  readonly currencies: readonly string[];
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
}

export function MoneyInput({
  label,
  currencyLabel,
  value,
  onValueChange,
  currency,
  onCurrencyChange,
  currencies,
  hint,
  error,
  ...input
}: MoneyInputProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  /* A currency already chosen stays offered even if it has since been
   * disabled. Prepended rather than sorted in, so it is obvious it is the
   * record's own rather than one of the workspace's. */
  const options =
    currency === '' || currencies.includes(currency) ? currencies : [currency, ...currencies];

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="amount">
        {/* `type="text"` with `inputmode="decimal"`, never `type="number"`.
            A number input silently discards what it cannot parse, and the
            separators differ by locale — `1.234,50` is one thousand in Turkish
            and one point two in English. Losing the only copy of what somebody
            meant is worse than showing them an error about it. */}
        <input
          {...input}
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className="field__control"
          value={value}
          onChange={(changed) => onValueChange(changed.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
        <select
          className="amount__cur"
          aria-label={currencyLabel}
          value={currency}
          onChange={(changed) => onCurrencyChange(changed.target.value)}
        >
          {options.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>
      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
